import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Check, X, MessageCircle, Instagram, Volume2, VolumeX,
  Dumbbell, Salad, Video, CalendarCheck, Radar,
} from "lucide-react";
import { OneRmCalc, TdeeCalc } from "@/components/Calculators";

const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "919000000000";
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hey Nimish, I want to know more about your 1:1 coaching.")}`;
const CONSULT_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hey Nimish, I want to book the ₹2,000 consultation call (up to 30 min). Send me your UPI details and let's lock a time.")}`;

const EASE = [0.16, 1, 0.3, 1];

const SoundToggle = () => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const a = new Audio("/music.mp3");
    a.loop = true;
    a.volume = 0.35;
    audioRef.current = a;
    const start = () => {
      a.play().then(() => setPlaying(true)).catch(() => {});
    };
    window.addEventListener("pointerdown", start, { once: true });
    window.addEventListener("keydown", start, { once: true });
    window.addEventListener("wheel", start, { once: true });
    window.addEventListener("touchmove", start, { once: true });
    window.addEventListener("scroll", start, { once: true });
    return () => a.pause();
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  return (
    <button
      onClick={toggle}
      data-testid="sound-toggle-btn"
      aria-label={playing ? "Mute music" : "Play music"}
      className={`fixed bottom-5 left-5 z-50 flex items-center gap-2.5 border px-4 py-3 font-mono-alt text-[10px] tracking-[0.25em] uppercase backdrop-blur-xl transition-colors ${
        playing
          ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]"
          : "border-white/20 bg-black/60 text-zinc-400 hover:text-white"
      }`}
    >
      {playing ? <Volume2 size={14} /> : <VolumeX size={14} />}
      {playing ? "Sound on" : "Sound off"}
    </button>
  );
};

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const ChapterTag = ({ num, label }) => (
  <div className="flex items-center gap-3 mb-8">
    <span className="font-mono-alt text-xs tracking-[0.3em] text-[#ccff00]">{num}</span>
    <span className="h-px w-12 bg-[#ccff00]/60" />
    <span className="font-mono-alt text-xs tracking-[0.3em] uppercase text-zinc-500">{label}</span>
  </div>
);

const deliverables = [
  { icon: Dumbbell, title: "Individualized Training", desc: "Built around your goal, your schedule, your equipment. Progressive overload that actually progresses. Heavy compounds, smart accessories." },
  { icon: Salad, title: "Personalized Nutrition", desc: "Calories and macros that fit your life. Adjusted when your data says so. Not a template." },
  { icon: Video, title: "Technique Analysis", desc: "Send your lifts. I tear your form down and rebuild it. Safer reps, bigger numbers." },
  { icon: CalendarCheck, title: "Weekly Check-ins", desc: "Weight, photos, performance. Reviewed every week. Your plan evolves as you do." },
  { icon: Radar, title: "Real Accountability", desc: "Direct line to me. You slip, I pull you back. You stall, we fix it." },
];

const processSteps = [
  { num: "01", name: "Assess", desc: "Your physique, history, lifestyle, goals. I learn everything before I write a single set." },
  { num: "02", name: "Build", desc: "Training and nutrition built around you. Your body, your schedule, your equipment." },
  { num: "03", name: "Execute", desc: "You lift heavy. I watch every rep you send and fix your form." },
  { num: "04", name: "Adjust", desc: "Stalls get killed fast. Your plan changes the moment your data says so." },
  { num: "05", name: "Transform", desc: "No restarts. No endless plateaus. Just strength that keeps climbing." },
];

const forYou = [
  "You're serious about getting strong",
  "You'll follow the plan, not vibes",
  "You want coaching, not a template",
  "You'll track your lifts and own your numbers",
  "You want someone watching every single week",
];

const notForYou = [
  "You want a magic 30-day fix",
  "You won't track anything",
  "You just want a PDF sent to you",
  "You're shopping for the cheapest coach",
];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  useEffect(() => {
    let lenis;
    let raf;
    let active = true;
    import("lenis").then(({ default: Lenis }) => {
      if (!active) return;
      lenis = new Lenis({ lerp: 0.09 });
      const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
    });
    return () => { active = false; if (raf) cancelAnimationFrame(raf); if (lenis) lenis.destroy(); };
  }, []);

  return (
    <div className="bg-[#0a0a0c] text-[#f4f4f6] min-h-screen" data-testid="landing-page">
      <SoundToggle />
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-24 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="block py-2">
            <img src="/logo.png" alt="Strength Syndicate" className="h-20 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-8 font-mono-alt text-[11px] tracking-[0.25em] uppercase text-zinc-400">
            <a href="#included" className="hover:text-white transition-colors" data-testid="nav-link-included">Included</a>
            <a href="#process" className="hover:text-white transition-colors" data-testid="nav-link-process">Process</a>
            <a href="#fit" className="hover:text-white transition-colors" data-testid="nav-link-fit">Who It's For</a>
          </div>
          <Link
            to="/apply"
            data-testid="nav-apply-btn"
            className="group flex items-center gap-2 bg-[#ccff00] hover:bg-[#a8d600] text-black font-mono-alt text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 transition-colors"
          >
            Apply
            <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <header ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: imgY }}>
          <img
            src="https://images.unsplash.com/photo-1709315957145-a4bad1feef28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxhdGhsZXRpYyUyMG1hbiUyMGxpZnRpbmclMjB3ZWlnaHRzJTIwZ3ltJTIwZGFyayUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4ODkyNzMzOXww&ixlib=rb-4.1.0&q=85"
            alt="Athlete training under spotlight"
            className="w-full h-[120%] object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-[#0a0a0c]/55" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 60% at 30% 40%, transparent 0%, #0a0a0c 90%)" }} />
        </motion.div>

        <motion.div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-24 pt-40 w-full" style={{ y: contentY }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="flex items-center gap-3 mb-8"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 bg-[#ccff00] animate-pulse-dot" />
            <span className="font-mono-alt text-[11px] sm:text-xs tracking-[0.3em] uppercase text-zinc-400">
              Limited client slots accepted · High-touch 1-on-1
            </span>
          </motion.div>

          <h1 className="font-display uppercase leading-[0.92] tracking-tight text-[13vw] sm:text-[9vw] lg:text-[7.5rem]" data-testid="hero-headline">
            <MaskedLine delay={0.35}>Stop guessing.</MaskedLine>
            <MaskedLine delay={0.5} className="text-[#ccff00]">Lift heavy.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed"
            data-testid="hero-subheadline"
          >
            1 on 1 coaching for lifters who want to get brutally strong and look like it.
            Your training, your nutrition, your technique. Watched and adjusted every single week.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/apply"
              data-testid="hero-apply-btn"
              className="group flex items-center gap-3 bg-[#ccff00] hover:bg-[#a8d600] text-black font-mono-alt text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors"
            >
              Apply for coaching
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group flex items-center gap-3 border border-white/20 hover:border-[#25d366] hover:text-[#25d366] text-zinc-300 font-mono-alt text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors"
            >
              <MessageCircle size={16} />
              WhatsApp me
            </a>
          </motion.div>
        </motion.div>
      </header>

      {/* COACH */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="coach-section">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 border border-[#ccff00]/30 pointer-events-none" />
              <img
                src="/coach.jpg"
                alt="Nimish Gulati, Founder of Strength Syndicate"
                className="w-full aspect-[4/5] object-cover object-top grayscale hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-[#0a0a0c]/85 backdrop-blur-md border-t border-white/10">
                <p className="font-display uppercase text-xl">Nimish Gulati</p>
                <p className="font-mono-alt text-[11px] tracking-[0.25em] uppercase text-zinc-400 mt-1">Founder · Strength Syndicate</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <ChapterTag num="CH.01" label="Your coach" />
            <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.95]">
              I lift heavy. <span className="text-[#ccff00]">My clients get strong.</span>
            </h2>
            <p className="mt-8 text-base md:text-lg text-zinc-300 leading-relaxed">
              Get brutally strong, finally lean out, or rebuild how you look and perform.
              Every week I review your numbers, your videos and your check-in, then I tell
              you exactly what changes.
            </p>
            <div className="mt-10 pt-8 border-t border-white/10 flex items-center gap-6" data-testid="assistant-coach">
              <img
                src="/assistant.jpg"
                alt="Amandeep Singh, Assistant Coach at Strength Syndicate"
                className="w-36 h-48 sm:w-44 sm:h-56 object-cover object-top grayscale hover:grayscale-0 transition-all duration-700 border border-white/10 shrink-0"
              />
              <div>
                <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-500">Assistant Coach</p>
                <p className="font-display uppercase text-xl mt-1">Amandeep Singh</p>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
                  4+ years coaching. Certified personal trainer. Rehabilitation and corrective exercise certified.
                </p>
              </div>
            </div>
            <Link
              to="/apply"
              data-testid="coach-apply-btn"
              className="group mt-10 inline-flex items-center gap-3 bg-[#ccff00] hover:bg-[#a8d600] text-black font-mono-alt text-xs tracking-[0.2em] uppercase px-8 py-4 transition-colors"
            >
              Start your application
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PAID CONSULT */}
      <section className="border-y border-white/10 bg-[#0d0d10]" data-testid="consult-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-16 grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <Reveal>
            <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-[#e5a93b] mb-4">Not ready for full coaching?</p>
            <h2 className="font-display uppercase text-3xl sm:text-4xl leading-[0.95]">
              Consultation call. <span className="text-[#ccff00]">₹2,000.</span>
            </h2>
            <p className="mt-4 max-w-lg text-base text-zinc-400 leading-relaxed">
              Up to 30 minutes. Your training, your diet, your stuck points. UPI before the call. You leave with a plan.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={CONSULT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="consult-whatsapp-btn"
              className="group inline-flex items-center gap-3 bg-[#25d366] hover:bg-[#1ebe57] text-black font-mono-alt text-xs tracking-[0.2em] uppercase px-8 py-5 transition-colors w-fit"
            >
              <MessageCircle size={16} />
              Book on WhatsApp
            </a>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 bg-[#0d0d10] py-4 overflow-hidden" data-testid="marquee-ribbon">
        <div className="animate-marquee flex whitespace-nowrap w-max">
          {[0, 1].map((i) => (
            <span key={i} className="font-display uppercase text-lg sm:text-xl tracking-wider text-zinc-600 flex items-center">
              {["Strength", "Heavy Lifting", "Physique", "Fat Loss", "Technique", "Nutrition", "Accountability", "PRs"].map((w) => (
                <span key={w} className="flex items-center">
                  <span className="px-6">{w}</span>
                  <span className="text-[#ccff00] text-xs">●</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* WHAT'S INCLUDED */}
      <section id="included" className="max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36" data-testid="included-section">
        <Reveal>
          <ChapterTag num="CH.02" label="What you get" />
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            I don't sell workout PDFs. <span className="text-stroke">I build lifters.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {deliverables.map((d, i) => (
            <Reveal key={d.title} delay={i * 0.08} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <div
                data-testid={`deliverable-card-${i}`}
                className="group h-full bg-[#121216] border border-white/10 p-8 hover:border-[#ccff00]/50 hover:-translate-y-1 transition-all duration-300"
              >
                <d.icon size={26} className="text-[#ccff00] mb-6 transition-transform duration-300 group-hover:scale-110" />
                <h3 className="font-display uppercase text-xl tracking-wide mb-3">{d.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{d.desc}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.4}>
            <Link
              to="/apply"
              data-testid="included-apply-card"
              className="group flex flex-col justify-between h-full min-h-[220px] bg-[#ccff00] hover:bg-[#a8d600] text-black p-8 transition-colors"
            >
              <span className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-black/60">Ready when you are</span>
              <span className="flex items-end justify-between">
                <span className="font-display uppercase text-2xl leading-tight">Apply for<br />coaching</span>
                <ArrowRight size={28} className="transition-transform group-hover:translate-x-2" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PAIN MANIFESTO */}
      <section className="relative border-y border-white/10 bg-[#0d0d10] overflow-hidden" data-testid="pain-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <ChapterTag num="CH.03" label="The real problem" />
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              You don't need another workout plan.
            </h2>
            <p className="mt-8 text-base md:text-lg text-zinc-300 leading-relaxed max-w-lg">
              The problem isn't effort. It's that nobody is watching your numbers and telling you
              what to change. <span className="text-white font-semibold">That's my job.</span>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-white/10 bg-[#121216] p-8 sm:p-10">
              <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-6">You've probably already tried</p>
              <ul className="space-y-5">
                {[
                  "Random workouts, zero progression",
                  "Copying influencer routines",
                  "A new diet every few weeks",
                  "Grinding hard with nothing to show",
                  "Fired up Monday, gone by Friday",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4 text-zinc-300" data-testid={`pain-item-${i}`}>
                    <X size={18} className="text-[#ccff00] mt-0.5 shrink-0" />
                    <span className="text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36" data-testid="process-section">
        <Reveal>
          <ChapterTag num="CH.04" label="The process" />
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            A system, <span className="text-stroke">not a guess.</span>
          </h2>
        </Reveal>
        <div className="mt-16">
          {processSteps.map((p, i) => (
            <Reveal key={p.num} delay={i * 0.05}>
              <div
                data-testid={`process-step-${p.num}`}
                className="group grid grid-cols-[auto_1fr] sm:grid-cols-[120px_200px_1fr] gap-6 sm:gap-10 items-baseline border-t border-white/10 py-10 hover:bg-white/[0.02] transition-colors px-2 sm:px-4"
              >
                <span className="font-mono-alt text-sm text-[#ccff00] tracking-widest">{p.num}</span>
                <h3 className="font-display uppercase text-2xl sm:text-3xl tracking-wide group-hover:text-[#ccff00] transition-colors">{p.name}</h3>
                <p className="col-span-2 sm:col-span-1 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-2xl">{p.desc}</p>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-white/10" />
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section id="fit" className="border-y border-white/10 bg-[#0d0d10]" data-testid="fit-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36">
          <Reveal>
            <ChapterTag num="CH.05" label="Qualification" />
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
              This isn't for everyone. <span className="text-stroke">That's the point.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 gap-4">
            <Reveal>
              <div className="h-full border border-white/10 bg-[#121216] p-8 sm:p-10" data-testid="for-list">
                <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-[#e5a93b] mb-8">This is for you if</p>
                <ul className="space-y-6">
                  {forYou.map((item, i) => (
                    <li key={i} className="flex items-start gap-4" data-testid={`for-item-${i}`}>
                      <span className="w-6 h-6 flex items-center justify-center bg-[#ccff00]/15 text-[#ccff00] shrink-0 mt-0.5">
                        <Check size={14} />
                      </span>
                      <span className="text-base text-zinc-200">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="h-full border border-white/10 bg-[#0a0a0c] p-8 sm:p-10" data-testid="not-for-list">
                <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-8">This isn't for you if</p>
                <ul className="space-y-6">
                  {notForYou.map((item, i) => (
                    <li key={i} className="flex items-start gap-4" data-testid={`not-for-item-${i}`}>
                      <X size={18} className="text-zinc-600 shrink-0 mt-1" />
                      <span className="text-base text-zinc-500">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-10 pt-8 border-t border-white/10 text-sm text-zinc-500 leading-relaxed">
                  I'm not trying to convince everyone. I'm looking for the person who reads this and thinks:
                  <span className="text-zinc-300"> "This is exactly what I need."</span>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>


      <section className="border-t border-white/10 bg-[#0d0d10]" data-testid="proof-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28">
          <Reveal>
            <ChapterTag num="CH.06" label="Proof" />
            <div className="grid md:grid-cols-[1fr_auto] gap-10 items-end">
              <div>
                <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.95]">
                  Real work. <span className="text-[#ccff00]">Real results.</span>
                </h2>
                <p className="mt-6 max-w-lg text-base md:text-lg text-zinc-300 leading-relaxed">
                  Straight from client check-ins. More on Instagram in the <span className="text-white font-semibold">"Coaching"</span> highlight.
                </p>
              </div>
              <a
                href="https://www.instagram.com/nimishhgulati"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-proof-btn"
                className="group inline-flex items-center gap-3 border border-white/20 hover:border-[#ccff00] px-8 py-5 font-mono-alt text-xs tracking-[0.2em] uppercase text-zinc-200 hover:text-white transition-colors w-fit"
              >
                <Instagram size={18} className="text-[#ccff00]" />
                @nimishhgulati
                <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </Reveal>

          <div className="mt-14 columns-1 sm:columns-2 lg:columns-3 gap-4" data-testid="proof-gallery">
            {[
              { src: "/proof1.jpg", label: "-8 KG · 10 WEEKS · FAT LOSS" },
              { src: "/proof4.jpg", label: "BACK · 1 MONTH IN" },
              { src: "/proof3.jpg", label: "SAME BODYWEIGHT · 20 WEEKS APART" },
              { src: "/proof2.jpg", label: "THE BIG CUT · SIDE PROFILE" },
              { src: "/proof5.jpg", label: "BACK GROWTH · CHECK-IN REVIEW" },
            ].map((p, i) => (
              <Reveal key={p.src} delay={i * 0.06} className="mb-4 break-inside-avoid">
                <div
                  data-testid={`proof-card-${i}`}
                  className="group relative overflow-hidden border border-white/10 hover:border-[#ccff00]/60 transition-colors duration-300 bg-[#121216]"
                >
                  <img
                    src={p.src}
                    alt={p.label}
                    loading="lazy"
                    className="w-full transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-[#0a0a0c]/85 backdrop-blur-md border-t border-white/10">
                    <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#e5a93b]">{p.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLS */}
      <section className="border-t border-white/10" data-testid="tools-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28">
          <Reveal>
            <ChapterTag num="CH.07" label="Free tools" />
            <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.95]">
              Run your numbers. <span className="text-stroke">Free.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid lg:grid-cols-2 gap-4">
            <Reveal><OneRmCalc /></Reveal>
            <Reveal delay={0.1}><TdeeCalc /></Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative border-t border-white/10 overflow-hidden" data-testid="final-cta-section">
        <img
          src="https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxhdGhsZXRpYyUyMG1hbiUyMGxpZnRpbmclMjB3ZWlnaHRzJTIwZ3ltJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg4OTI3MzQ2fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-[#0a0a0c]/70" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-32 sm:py-44 text-center">
          <Reveal>
            <p className="font-mono-alt text-[11px] tracking-[0.35em] uppercase text-[#ccff00] mb-8">Limited client slots</p>
            <h2 className="font-display uppercase text-[11vw] sm:text-6xl lg:text-7xl leading-[0.95]">
              Your move.
            </h2>
            <p className="mt-8 max-w-md mx-auto text-base text-zinc-300 leading-relaxed">
              Two minutes to apply. If we're a fit, we talk on WhatsApp and map out your next PR.
            </p>
            <Link
              to="/apply"
              data-testid="final-apply-btn"
              className="group mt-10 inline-flex items-center gap-3 bg-[#ccff00] hover:bg-[#a8d600] text-black font-mono-alt text-xs tracking-[0.2em] uppercase px-10 py-5 transition-colors"
            >
              Apply for coaching
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <img src="/logo.png" alt="Strength Syndicate" className="h-28 w-auto" />
          <p className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-600">
            Personalized coaching for people who are done guessing
          </p>
          <div className="flex items-center gap-6">
            <a href="https://www.instagram.com/nimishhgulati" target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-[#ccff00] transition-colors" data-testid="footer-instagram-link">
              <Instagram size={18} />
            </a>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-[#25d366] transition-colors" data-testid="footer-whatsapp-link">
              <MessageCircle size={18} />
            </a>
            <Link to="/admin/login" className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-zinc-600 hover:text-zinc-300 transition-colors" data-testid="footer-admin-link">
              Coach Login
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
